import { onUnmounted, Ref, ref } from "@vue/composition-api";
import { EVENT_NAMES } from "@scrile/api-provider";
import { ErrorDTO } from "@scrile/api-provider/dist/lib/apiErrorHandler";
import { AiMessage } from "@scrile/api-provider/dist/projects/scrileConnect/AiMessage";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { attachmentFileAdapter } from "@/lib/fileHelpers";
import { handleRequestError } from "@/lib/handleRequestError";
import { $t } from "@/lib/i18nHelpers";
import { NotificationData, PredefinedMessage } from "@/types";
import { ERRORS, EVENTS } from "@/const";

const MAX_EMPTY_MESSAGE_RETRIES = 9;
const errorMessage = $t("Unable to generate response. Please try again.");

export default function useAIMessages(threadId: Ref<string | undefined>) {
  const { authUser, authorities } = useAuthUser();

  const aiMessages = ref<AiMessage[]>([]);
  const processing = ref(false);
  const showFillAIQuestionary = ref(false);
  const subscription = ref();
  let unsubscribe = () => {};
  let emptyMessageRetryCount = 0;

  const generateAiMessage = async () => {
    if (!threadId.value) return;
    await providers.AiMessageProvider.messageGenerate({ data: { threadId: threadId.value } });
  };

  const onNewMessageReceived = async (message: AiMessage) => {
    if (!message.succeed || emptyMessageRetryCount >= MAX_EMPTY_MESSAGE_RETRIES) {
      processing.value = false;
      emptyMessageRetryCount = 0;
      emitter.emit<NotificationData>(EVENTS.NOTIFICATION_SHOW, { text: errorMessage, position: "top" });
      return;
    }
    if (message.text.trim() === "") {
      emptyMessageRetryCount++;
      return generateAiMessage();
    }
    emptyMessageRetryCount = 0;
    aiMessages.value.push(message);
    processing.value = false;
  };

  const onError = (e: ErrorDTO[]) => {
    e.forEach(error => emitter.emit(EVENT_NAMES.REQUEST_STATUS, error));
    subscription.value = null;
    emitter.emit<NotificationData>(EVENTS.NOTIFICATION_SHOW, { text: errorMessage, position: "top" });
    processing.value = false;
  };

  const clearAIMessage = () => (aiMessages.value = []);

  const getPredefinedMessage = (index: number = 0): PredefinedMessage => {
    return {
      text: aiMessages.value[index]?.text ?? "",
      files: attachmentFileAdapter(aiMessages.value[index]?.files ?? []),
    };
  };

  const onGenerateAIClick = async (replace: boolean = false) => {
    if (!authUser.value?.id || !threadId.value || !checkAuthorities(authorities.value, ["getAiMessage"])) return;
    if (replace) clearAIMessage();
    emptyMessageRetryCount = 0;
    processing.value = true;
    if (!subscription.value) {
      subscription.value = await providers.AiMessageProvider.subscribeToThread(
        threadId.value,
        onNewMessageReceived,
        onError
      );
      unsubscribe = () => subscription.value?.unsubscribe();
    }
    try {
      await generateAiMessage();
    } catch (e) {
      handleRequestError(e, [
        {
          comparator: e => e.details?.messageTemplate === ERRORS.AI_QUESTIONARY_NOT_COMPLETED,
          onSuccessCB: () => {
            processing.value = false;
            showFillAIQuestionary.value = true;
          },
        },
      ]);
    }
  };

  onUnmounted(() => unsubscribe());

  return {
    aiMessages,
    processing,
    showFillAIQuestionary,
    onGenerateAIClick,
    clearAIMessage,
    getPredefinedMessage,
  };
}
